import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { usePhotoModal, useLightboxImageState } from 'hooks'
import { buildTeamShareUrl, showcaseItemKey, findShowcaseIndexByKey } from 'utils'
import { trackEvent } from 'utils/analytics'
import { detectVideoPlatform, getVideoEmbedProps, getVideoThumbnailUrl } from 'utils/videoUtils'
import ShareButton from './ShareButton'

export default function TeamAchievementsSection({ achievements, colors, translations, teamSlug, initialShowcaseKey, currentUser, teamId, shareTranslations }) {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const touchStartRef = useRef(null)

  const {
    isOpen, currentIndex, photos,
    openModal, closeModal,
    goToPrevious, goToNext,
    modalRef, handleOverlayClick
  } = usePhotoModal()

  const currentImage = isOpen && photos.length > 0 ? photos[currentIndex] : null
  const hasMultiplePhotos = photos.length > 1
  const shareKey = currentImage ? showcaseItemKey(currentImage) : ''

  // Image loading/error UI, driven by the real <img> so a cached (already
  // `complete`) image never leaves the spinner hanging. Videos skip loading.
  const currentIsVideo = !!(currentImage && currentImage.type === 'video' && currentImage.video_url)
  const {
    isLoading: isImageLoading,
    hasError: imageError,
    imgRef,
    onLoad: handleImageLoad,
    onError: handleImageError
  } = useLightboxImageState(`${isOpen}-${currentIndex}`, currentIsVideo)

  // Reset description expanded state when navigating
  useEffect(() => {
    setIsDescriptionExpanded(false)
  }, [currentIndex])

  // Keyboard arrow navigation
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPrevious() }
      if (e.key === 'ArrowRight') { e.preventDefault(); goToNext() }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, goToPrevious, goToNext])

  // Filter out entries without valid media (image url or video url)
  const validAchievements = (achievements || []).filter(item =>
    item && (item.url || (item.type === 'video' && item.video_url))
  )

  // Deep link: auto-open the lightbox on the shared item (?showcase=<key>).
  // Matched by content key so it survives reordering; if the item is gone we
  // stay silent rather than open an unrelated one. The guard flips only after a
  // successful match, so a not-yet-ready list won't burn the one-shot.
  const didAutoOpenRef = useRef(false)
  useEffect(() => {
    if (didAutoOpenRef.current || !initialShowcaseKey) return
    const idx = findShowcaseIndexByKey(validAchievements, initialShowcaseKey)
    if (idx < 0) return
    didAutoOpenRef.current = true
    openModal(validAchievements, idx)
  }, [initialShowcaseKey, validAchievements, openModal])

  // NOTE: every hook above must stay above this early return (Rules of Hooks).
  if (validAchievements.length === 0) {
    return null
  }

  const handleImageClick = (index) => {
    trackEvent('achievement_click', {
      achievement_title: validAchievements[index]?.title || 'untitled',
      achievement_type: validAchievements[index]?.type || 'image',
      team_slug: teamSlug
    })
    openModal(validAchievements, index)
  }

  const onTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX
  }

  const onTouchEnd = (e) => {
    if (touchStartRef.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current
    if (Math.abs(deltaX) > 50) {
      deltaX > 0 ? goToPrevious() : goToNext()
    }
    touchStartRef.current = null
  }

  // Get video thumbnail info for a video item
  const getVideoThumb = (item) => {
    if (!item.video_url) return null
    const detection = detectVideoPlatform(item.video_url)
    if (!detection) return null
    return {
      thumbnailUrl: getVideoThumbnailUrl(detection.platform, detection.videoId),
      platformName: detection.platformName
    }
  }

  // Shared media cell renderer (image or video)
  const renderMediaCell = (item, index, className, showOverlay = false) => {
    const isVideo = item.type === 'video' && item.video_url
    const videoThumb = isVideo ? getVideoThumb(item) : null

    return (
      <div
        key={index}
        className={`relative overflow-hidden cursor-pointer group ${className}`}
        onClick={() => handleImageClick(index)}
      >
        {isVideo ? (
          videoThumb?.thumbnailUrl ? (
            <img
              src={videoThumb.thumbnailUrl}
              alt={item.title || `Achievement ${index + 1}`}
              className="w-full h-full object-contain bg-black transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500 text-sm">{videoThumb?.platformName || 'Video'}</span>
            </div>
          )
        ) : (
          <img
            src={item.url}
            alt={item.title || `Achievement ${index + 1}`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
        {/* Play button overlay for video items */}
        {isVideo && !showOverlay && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}
        {item.title && !showOverlay && (
          <div className="absolute top-0 left-0 right-0 px-4 py-3 bg-black/70 backdrop-blur-sm">
            <p className="text-white text-base font-semibold drop-shadow-md truncate">{item.title}</p>
          </div>
        )}
        {showOverlay && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="text-white text-lg font-semibold">
              {(translations?.showAllItems || 'Show all %{count} items').replace('%{count}', validAchievements.length)}
            </span>
          </div>
        )}
      </div>
    )
  }

  // Data preparation
  const bigImage = validAchievements[0]
  const smallImages = validAchievements.slice(1, 3)
  const showOverlay = validAchievements.length > 3

  // Render grid based on image count
  const renderGrid = () => {
    if (validAchievements.length === 1) {
      return renderMediaCell(bigImage, 0, 'aspect-[4/3] rounded-lg max-h-[400px]')
    }

    if (validAchievements.length === 2) {
      return (
        <div className="grid grid-cols-2 gap-2">
          {renderMediaCell(validAchievements[0], 0, 'aspect-[4/3] rounded-lg')}
          {renderMediaCell(validAchievements[1], 1, 'aspect-[4/3] rounded-lg')}
        </div>
      )
    }

    // 3+ images: dual layout
    return (
      <>
        {/* Desktop layout */}
        <div className="hidden lg:flex gap-2 h-[400px]">
          <div className="w-3/5 min-w-0">
            {renderMediaCell(bigImage, 0, 'rounded-lg w-full h-full')}
          </div>
          <div className="w-2/5 min-w-0 flex flex-col gap-2">
            {smallImages.map((img, i) => {
              const isLast = i === smallImages.length - 1
              return renderMediaCell(img, i + 1, 'rounded-lg flex-1 min-h-0', isLast && showOverlay)
            })}
          </div>
        </div>

        {/* Mobile layout */}
        <div className="lg:hidden flex flex-col gap-1">
          {renderMediaCell(bigImage, 0, 'aspect-[4/3] rounded-lg')}
          <div className="grid grid-cols-2 gap-1">
            {smallImages.map((img, i) => {
              const isLast = i === smallImages.length - 1
              return renderMediaCell(img, i + 1, 'aspect-square rounded-lg', isLast && showOverlay)
            })}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <section className="py-8 border-t border-gray-200">
        <div className="mb-6">
          <h2 className="text-[22px] font-semibold" style={{ color: '#222222' }}>{translations?.title || 'Teaching Showcase'}</h2>
          <p className="text-sm mt-1" style={{ color: '#717171' }}>{translations?.subtitle || 'Teaching results, professional sharing and videos'}</p>
        </div>
        {renderGrid()}
      </section>

      {/* Full-screen Gallery */}
      {isOpen && photos.length > 0 && createPortal(
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex flex-col"
          onClick={handleOverlayClick}
          ref={modalRef}
          role="dialog"
          aria-modal="true"
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); closeModal() }}
            className="absolute top-4 right-4 z-50 p-4 min-w-[44px] min-h-[44px] text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 rounded-full transition-colors cursor-pointer"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous button */}
          {hasMultiplePhotos && (
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevious() }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-4 min-w-[44px] min-h-[44px] text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 rounded-full transition-colors cursor-pointer"
              aria-label="Previous"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Next button */}
          {hasMultiplePhotos && (
            <button
              onClick={(e) => { e.stopPropagation(); goToNext() }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-4 min-w-[44px] min-h-[44px] text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 rounded-full transition-colors cursor-pointer"
              aria-label="Next"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Counter */}
          {hasMultiplePhotos && (
            <div className="absolute top-4 left-4 z-10 px-3 py-2 bg-black/50 text-white rounded-full text-sm">
              {currentIndex + 1} / {photos.length}
            </div>
          )}

          {/* Share this item — deep link back to the current showcase item.
              Sits below the counter on the left so it never collides with the
              close button (top-right) or the counter (top-left). */}
          {currentImage && teamSlug && (
            <div
              className="absolute top-16 left-4 z-50 flex items-center max-w-[calc(100%-2rem)] px-3 py-2 bg-black/40 rounded-full"
              onClick={(e) => e.stopPropagation()}
            >
              <ShareButton
                url={buildTeamShareUrl(`/${teamSlug}`, { showcase: shareKey }, currentUser?.inviteCode)}
                title={currentImage.title || ''}
                colors={colors}
                translations={shareTranslations}
                utmContent={`showcase-${shareKey}`}
                currentUser={currentUser}
                teamId={teamId}
              />
            </div>
          )}

          {/* Image area — flex-1 so it takes remaining space after text bar */}
          <div
            className="flex-1 min-h-0 relative flex items-center justify-center px-4 pt-4"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {currentImage && currentImage.type === 'video' && currentImage.video_url && detectVideoPlatform(currentImage.video_url) ? (
              <div className="w-full max-w-4xl aspect-video">
                <iframe
                  key={currentIndex}
                  {...getVideoEmbedProps(detectVideoPlatform(currentImage.video_url))}
                  className="w-full h-full rounded-lg"
                />
              </div>
            ) : (
              <>
                {/* Loading spinner */}
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}

                {/* Error state */}
                {imageError && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-white">{translations?.loadError || 'Failed to load'}</p>
                    </div>
                  </div>
                )}

                {/* Current image */}
                {currentImage && (
                  <img
                    ref={imgRef}
                    src={currentImage.url}
                    alt={currentImage.title || `Achievement ${currentIndex + 1}`}
                    className={`max-w-full max-h-full object-contain transition-opacity duration-200 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    draggable="false"
                    style={{ display: imageError ? 'none' : 'block' }}
                  />
                )}
              </>
            )}
          </div>

          {/* Title and description bar — shrink-0, never overlaps image */}
          {currentImage && (currentImage.title || currentImage.description) && (
            <div className="shrink-0 w-full max-w-4xl mx-auto px-4 py-3" onClick={(e) => e.stopPropagation()}>
              <div className="text-left text-white bg-black/70 rounded-lg p-4">
                {currentImage.title && (
                  <h3 className="text-lg font-semibold line-clamp-2">{currentImage.title}</h3>
                )}
                {currentImage.description && (
                  <>
                    <p className={`text-sm text-gray-300 mt-1 whitespace-pre-line ${!isDescriptionExpanded ? 'line-clamp-3' : ''}`}>
                      {currentImage.description}
                    </p>
                    {currentImage.description.length > 80 && (
                      <button
                        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                        className="text-sm text-gray-400 hover:text-white mt-1 cursor-pointer"
                      >
                        {isDescriptionExpanded ? (translations?.showLess || 'Show less') : (translations?.showMore || 'Show more')}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}
